"use client";

import { METAFIELD_KEY_ALGOLIA_PURCHASE_TRACKING } from "@/constants";
import { SEARCH_COOKIE_NAMES } from "@/lib/_search-providers/constants";
import {
	AlgoliaSearchClientConfig,
	SearchFacet,
} from "@/lib/_search-providers/types";
import { getLineItemForProduct } from "@/utils/cart-utils";
import Cookies from "js-cookie";
import { chunk } from "lodash";
import aa, { InsightsMethodMap } from "search-insights";
import { v4 } from "uuid";
import EventBus, {
	EventCallback,
	EventName,
	Events,
} from "@/lib/_event-bus/event-bus";
import {
	ClickedPayload,
	ProductAddToCartPayload,
	ProductClickedPayload,
	ProductsViewedPayload,
	PurchasedPayload,
	QueryIdSetPayload,
	UserAuthenticatedPayload,
} from "@/lib/_event-bus/events-payloads.types";
import { createOrUpdateCartMetafield } from "@/lib/bigcommerce/api/cart/cart-metafields";
import {
	AddedToCartObjectIDsAfterSearchParams,
	AddedToCartObjectIDsParams,
	EventType,
	InitParams,
} from "./algolia-insights.types";

export class AlgoliaInsights {
	private enableLogging = false;
	private indexName: string;
	private localeSuffix: string;
	private anonymousUserToken: string;
	private userToken: string | undefined;
	private eventCache: Record<EventType, string | undefined> = {} as Record<
		EventType,
		string | undefined
	>;
	private queryId = "";

	constructor(config: AlgoliaSearchClientConfig) {
		let locale = config.locale;

		if (locale === "en") {
			locale = "en-US";
		}

		this.localeSuffix = locale;

		const indexName = `${config.baseIndex}-${config.storeHash}-${locale}`;
		this.indexName = indexName.replace(/^-/, "");

		this.anonymousUserToken = this.setupAnonymousUserToken();

		const params: InitParams = {
			appId: config.appId,
			apiKey: config.apiKey,
			userHasOptedOut: !this.hasConsent(),
			authenticatedUserToken: this.userToken,
		};

		this.attachEventListeners();
		aa("init", params);

		this.log({
			level: "debug",
			function: "constructor",
			args: ["queryId: ", this.queryId],
		});

		this.attachToWindow();
	}

	private hasExpectedLocaleSuffix(indexName: string) {
		return Boolean(indexName) && indexName.endsWith(`-${this.localeSuffix}`);
	}

	private hasValidObjectIDs(objectIDs: unknown) {
		if (!Array.isArray(objectIDs)) {
			return true;
		}

		if (!objectIDs.length) {
			return false;
		}

		return objectIDs.some(
			(objectID) => typeof objectID === "string" && objectID.trim() !== "",
		);
	}

	private formatPrice(value: unknown) {
		if (typeof value !== "number" || !Number.isFinite(value)) {
			return value;
		}

		return value.toFixed(2);
	}

	private normalizeEventParams(params: any) {
		if (!params || typeof params !== "object") {
			return params;
		}

		const normalizedParams = { ...params };

		if (Array.isArray(normalizedParams.objectData)) {
			normalizedParams.objectData = normalizedParams.objectData.map(
				(objectData: any) => {
					if (!objectData || typeof objectData !== "object") {
						return objectData;
					}

					const hasDiscount = objectData.discount !== undefined;

					return {
						...objectData,
						price: this.formatPrice(objectData.price),
						...(hasDiscount && {
							discount: this.formatPrice(objectData.discount),
						}),
					};
				},
			);
		}

		return normalizedParams;
	}

	private attachToWindow() {
		if (typeof window === "undefined") {
			return;
		}

		window.AlgoliaInsights = this;
	}

	private attachEventListeners() {
		if (typeof window === "undefined") {
			return;
		}

		const subscriptions = {
			[Events.userAuthenticated]: this.bind(this.handleUserAuthenticated),
			[Events.queryIdSet]: this.bind(this.handleQueryIdSet),
			[Events.productAddToCart]: this.bind(this.handleProductAddToCart),
			[Events.productClicked]: this.bind(this.handleProductClicked),
			[Events.productsViewed]: this.bind(this.handleProductsViewed),
			[Events.clicked]: this.bind(this.handleClickedEvent),
			[Events.purchased]: this.bind(this.handlePurchasedEvent),
		} as Record<EventName, EventCallback<EventName>>;

		EventBus.subscribeMultiple(subscriptions);
	}

	private bind(handler: Function) {
		return handler.bind(this);
	}

	private log(params: {
		level: "debug" | "error";
		function?: string;
		args: any;
	}) {
		if (!this.enableLogging) {
			return;
		}

		const functionPart = params.function ? `~ ${params.function}` : "";

		console[params.level](`🧠 ~ AlgoliaInsights ${functionPart}`, params.args);
	}

	private setupAnonymousUserToken(): string {
		if (typeof window === "undefined") {
			return "";
		}

		let token = Cookies.get(SEARCH_COOKIE_NAMES.anonymousUserToken);

		if (!token) {
			token = v4();
		}

		// update the cookie
		Cookies.set(SEARCH_COOKIE_NAMES.anonymousUserToken, token, {
			expires: 365 / 2, // 6 months
			path: "/",
			sameSite: "strict",
			secure: true,
		});

		return token;
	}

	private hasConsent(): boolean {
		if (typeof window === "undefined") {
			return true;
		}

		const onetrustConsentValue = "C0003";

		const onetrustActiveGroups = window.OnetrustActiveGroups;

		if (!onetrustActiveGroups) {
			return true;
		}

		return onetrustActiveGroups.includes(onetrustConsentValue);
	}

	private fireEvent(eventType: EventType, params: any) {
		const normalizedParams = this.normalizeEventParams(params);

		if (!this.hasExpectedLocaleSuffix(this.indexName)) {
			this.log({
				level: "error",
				function: "fireEvent",
				args: {
					message: "Skipping Algolia event due to malformed index name",
					indexName: this.indexName,
					expectedLocaleSuffix: this.localeSuffix,
				},
			});

			return;
		}

		if (!this.hasValidObjectIDs(normalizedParams?.objectIDs)) {
			this.log({
				level: "error",
				function: "fireEvent",
				args: {
					message: "Skipping Algolia event due to empty objectIDs",
					eventType,
					objectIDs: normalizedParams?.objectIDs,
				},
			});

			return;
		}

		// prevent duplicate events
		const cachedEvent = this.eventCache[eventType];

		if (cachedEvent === JSON.stringify(normalizedParams)) {
			return;
		}

		// cache the event for duplicate prevention
		this.eventCache[eventType] = JSON.stringify(normalizedParams);

		// add additional params to event params
		const eventData = {
			...normalizedParams,
			index: this.indexName,
			userToken: this.anonymousUserToken,
			authenticatedUserToken: this.userToken,
		};

		// send the event
		aa(eventType, eventData);

		this.log({
			level: "debug",
			function: "fireEvent",
			args: {
				eventType,
				eventData,
			},
		});

		this.attachToWindow();
	}

	public viewFilters(facets: SearchFacet[]) {
		const getActiveFacets = (facets: SearchFacet[]) => {
			const selectedFacetObjects = facets.filter((facet) =>
				facet.options.some((option) => option.isActive),
			);

			const selectedFacetValues = selectedFacetObjects.flatMap((facet) => {
				return facet.options
					.filter((option) => option.isActive)
					.map((option) => {
						return `${encodeURIComponent(facet.value)}:${encodeURIComponent(
							option.value,
						)}`;
					});
			});

			return selectedFacetValues;
		};

		const activeFacets = getActiveFacets(facets);

		if (!activeFacets.length) {
			return;
		}

		// the event can only handle a max of 10 facets
		const batches = chunk(activeFacets, 10);

		batches.forEach((batch) => {
			this.fireEvent("viewedFilters", {
				eventName: "Filter Viewed",
				filters: batch,
			});
		});
	}

	private handleQueryIdSet(payload: QueryIdSetPayload) {
		const { queryID } = payload;

		this.queryId = queryID;

		this.log({
			level: "debug",
			function: "handleQueryIdSet",
			args: { queryID },
		});
	}

	private handleUserAuthenticated(payload: UserAuthenticatedPayload) {
		const { customerId, storeHash } = payload;

		let userToken = "";

		if (customerId !== undefined && storeHash !== undefined) {
			userToken = `${customerId}-${storeHash}`;
		}

		aa("setUserToken", userToken);

		aa("getUserToken", {}, (err, userToken) => {
			this.userToken = String(userToken);

			this.log({
				level: "debug",
				function: "aa(getUserToken)",
				args: { userToken },
			});
		});
	}

	/**
	 * This method is called when a product is clicked.
	 * Responsable for firing the clickedObjectIDs or clickedObjectIDsAfterSearch event.
	 *
	 * clickedObjectIDsAfetSearch is only sent when a product is clicked on the search results page or search dropdown
	 *
	 * See https://www.algolia.com/doc/libraries/search-insights/clicked-object-ids-after-search or
	 * https://www.algolia.com/doc/libraries/search-insights/clicked-object-ids
	 *
	 * @param payload event payload from EventBus
	 * @returns void
	 */
	private handleProductClicked(payload: ProductClickedPayload) {
		const { product, context } = payload;

		const isNavigationEvent = context?.isNavigationEvent;

		// only fire the event if it is a navigation event
		if (!isNavigationEvent) {
			return;
		}

		const queryId = context?.queryId;
		const position = context?.position;

		const hasQueryId = queryId !== undefined;

		const hasAdditionalData = hasQueryId && position !== undefined;

		const eventType: keyof InsightsMethodMap = hasAdditionalData
			? "clickedObjectIDsAfterSearch"
			: "clickedObjectIDs";

		const eventParams = {
			eventName: context?.eventName || "Product Clicked",
			objectIDs: [product.graphqlId],
		} as any;

		if (hasAdditionalData) {
			eventParams.positions = [position];
			eventParams.queryID = queryId;
		}

		// if we handle a product click event, update the queryId if there is one set
		// so when can use it in other events that might require it
		this.queryId = hasQueryId ? queryId : "";

		this.fireEvent(eventType, eventParams);
	}

	/**
	 * This method is called when products are viewed.
	 * Responsable for firing the viewedObjectIDs event.
	 *
	 * See https://www.algolia.com/doc/libraries/search-insights/viewed-object-ids
	 *
	 * @param payload event payload from EventBus
	 * @returns void
	 */
	private handleProductsViewed(payload: ProductsViewedPayload) {
		const { products, context } = payload;

		const eventName = context?.eventName || "Product Viewed";
		const objectIDs = products
			.map((product) => product.graphqlId)
			.filter((id) => id !== undefined);

		// split objectIDs into batches of 20 as this the max the event can handle
		const batches = chunk(objectIDs, 20);

		batches.forEach((batch) => {
			this.fireEvent("viewedObjectIDs", {
				eventName,
				objectIDs: batch,
			});
		});
	}

	/**
	 * This method is called when a product is added to the cart.
	 * Responsible for firing the addedToCartObjectIDs or addedToCartObjectIDsAfterSearch event.
	 *
	 * See https://www.algolia.com/doc/libraries/search-insights/added-to-cart-object-ids-after-search or
	 * https://www.algolia.com/doc/libraries/search-insights/added-to-cart-object-ids
	 *
	 * @param payload event payload from EventBus
	 * @returns void
	 */
	private handleProductAddToCart(payload: ProductAddToCartPayload) {
		this.updateCartMetafield(payload);

		const { product, cart, context } = payload;

		const lineItem = getLineItemForProduct(cart, product);

		if (!lineItem) {
			return;
		}

		const queryID = context?.queryID || this.queryId;

		const eventType: keyof InsightsMethodMap = queryID
			? "addedToCartObjectIDsAfterSearch"
			: "addedToCartObjectIDs";

		const price = lineItem.quantity * lineItem.sale_price;

		let eventParams:
			| AddedToCartObjectIDsParams
			| AddedToCartObjectIDsAfterSearchParams = {
			eventName: "Product Added To Cart",
			objectIDs: [product.graphqlId],
			currency: cart.currency.code,
			discount: lineItem.discount_amount,
			objectData: [
				{
					price: price,
					discount: lineItem.discount_amount,
					quantity: lineItem.quantity,
				},
			],
			price: price,
			quantity: lineItem.quantity,
			value: price,
		} as AddedToCartObjectIDsParams;

		if (eventType === "addedToCartObjectIDsAfterSearch") {
			eventParams = {
				...eventParams,
				queryID,
				objectData: (eventParams as any).objectData.map((objectData: any) => {
					return {
						...objectData,
						queryID,
					};
				}),
			} as AddedToCartObjectIDsAfterSearchParams;
		}

		this.fireEvent(eventType, eventParams);
	}

	private async updateCartMetafield(payload: ProductAddToCartPayload) {
		const { product, cart, context } = payload;

		const lineItem = getLineItemForProduct(cart, product);

		// if we don't have a line item, we can't do anything
		if (!lineItem) {
			return;
		}

		// get the metafield from the cart, this might be undefined if it doesn't exist
		const metafield = cart.metafields.find(
			(metafield) => metafield.key === METAFIELD_KEY_ALGOLIA_PURCHASE_TRACKING,
		);

		let metafieldValue: string[] = [];

		try {
			metafieldValue = JSON.parse(metafield?.value || "[]") as string[];

			// ensure the value is an array
			if (!Array.isArray(metafieldValue)) {
				metafieldValue = [];
			}
		} catch (error) {
			// no need to do anything, we'll just overwrite it
		}

		// get the query ID from the context or from the queryId property
		const queryID = context?.queryID || this.queryId || undefined;

		const productIdentifier = `${lineItem.product_id}_${lineItem.variant_id}`;

		// try to find the array index of the current product in the metafield value
		const index = metafieldValue.findIndex((value) =>
			value.startsWith(productIdentifier),
		);

		// create the new line value to add to the array
		const lineValue = `${productIdentifier}${queryID ? `:${queryID}` : ""}`;

		// get the current value from the array
		const currentLineValue = metafieldValue[index];

		// if the current line value is the same as the new line value, we can return
		// as they match, no need to update the metafield
		if (currentLineValue === lineValue) {
			return;
		}

		// filter out the existing value from the metafield using the index
		metafieldValue = metafieldValue.filter((_, idx) => idx !== index);

		// add the new value
		metafieldValue.push(lineValue);

		// update the cart metafield
		// don't wait as it's not critical
		createOrUpdateCartMetafield({
			cartId: cart.id,
			value: metafieldValue,
			metafieldId: metafield?.id,
		});
	}

	/**
	 * Responsible for firing the clickedObjectIDs or clickedObjectIDsAfterSearch event.
	 *
	 * clickedObjectIDsAfterSearch is only sent when a product is clicked on the search results page or search dropdown
	 *
	 * See https://www.algolia.com/doc/libraries/search-insights/clicked-object-ids-after-search or
	 * https://www.algolia.com/doc/libraries/search-insights/clicked-object-ids
	 *
	 * @param payload event payload from EventBus
	 * @returns void
	 */
	private handleClickedEvent(payload: ClickedPayload) {
		const { type, context } = payload;

		let event = {
			eventType: "",
			payload: {},
		} as any;

		switch (type) {
			case "search-suggestion": {
				const objectId = context?.objectId;

				if (!objectId) {
					this.log({
						level: "error",
						function: `handleClickedEvent`,
						args: { type, context, reason: "Missing objectId" },
					});
					return;
				}

				const queryId = context?.queryId;
				const position = context?.position;

				const hasQueryId = queryId !== undefined;

				const hasAdditionalData = hasQueryId && position !== undefined;

				const eventType: keyof InsightsMethodMap = hasAdditionalData
					? "clickedObjectIDsAfterSearch"
					: "clickedObjectIDs";

				const eventParams = {
					eventName: "Search Suggestion Clicked",
					objectIDs: [objectId],
				} as any;

				if (hasAdditionalData) {
					eventParams.positions = [position];
					eventParams.queryID = queryId;
				}

				event = {
					eventType,
					payload: eventParams,
				};

				break;
			}
			default: {
				event = undefined;
			}
		}

		if (!event) {
			return;
		}

		this.fireEvent(event.eventType, event.payload);
	}

	/**
	 * Responsible for firing the purchasedObjectIDs or purchasedObjectIDsAfterSearch event.
	 *
	 * purchasedObjectIDsAfterSearch is only sent when a product is purchased.
	 *
	 * See https://www.algolia.com/doc/libraries/search-insights/purchased-object-ids
	 *
	 * @param payload event payload from EventBus
	 * @returns void
	 */
	private handlePurchasedEvent(payload: PurchasedPayload) {
		const { data, context } = payload;

		const eventName = context?.eventName || "Products Purchased";

		const batches = chunk(
			data.objectIDs
				.map((objectID, index) => ({
					objectID,
					objectData: data.objectData[index],
				}))
				.filter((item) => {
					return (
						typeof item.objectID === "string" &&
						item.objectID.trim() !== "" &&
						item.objectData !== undefined
					);
				}),
			20,
		);

		batches.forEach((batch) => {
			this.fireEvent("purchasedObjectIDsAfterSearch", {
				eventName,
				objectIDs: batch.map((item) => item.objectID),
				objectData: batch.map((item) => item.objectData),
				value: data.value,
				currency: data.currency,
			});
		});
	}
}
